'use client';

/**
 * Shared component pentru leaf items din sidebar-uri (main sidebar +
 * settings nav). Single source of truth pentru stilul de row interactive:
 * icon + label + (optional) right slot (badges/warnings).
 *
 * NU acoperă (intenționat — shape diferit):
 *  - Sub-items din grupuri colapsabile (pl-9 indent, icon h-4)
 *  - Primary CTA (Document nou, full-width orange button)
 *  - Collapsed mode (icon-only)
 *  - Pinned/Recent items (cu pin overlay)
 */

import Link from 'next/link';
import type { ElementType, ReactNode, MouseEvent } from 'react';
import { cn } from '@softfactura/ui';

export const NAV_LEAF_BASE =
  'flex items-center gap-3 rounded-lg px-3 py-1 text-sm font-medium transition-all duration-150';
export const NAV_LEAF_ACTIVE =
  'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm';
export const NAV_LEAF_INACTIVE =
  'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100';
export const NAV_ICON_BASE = 'h-5 w-5 flex-shrink-0';
export const NAV_ICON_ACTIVE = 'text-primary-600 dark:text-primary-400';

interface NavLeafItemProps {
  /** Render <Link> dacă prezent, altfel <button>. */
  href?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  icon: ElementType;
  label: string;
  active?: boolean;
  /** Slot pentru badges / warning indicators (right-aligned). */
  rightSlot?: ReactNode;
  /** Override pentru cazuri speciale (e.g. lg:mx-2, whitespace-nowrap). */
  className?: string;
  /** Truncate label cu ellipsis. Default true. */
  truncate?: boolean;
  /** Pass-through atribute uzuale. */
  'data-tour'?: string;
  'aria-current'?: 'page' | undefined;
  title?: string;
}

export function NavLeafItem({
  href,
  onClick,
  icon: Icon,
  label,
  active = false,
  rightSlot,
  className,
  truncate = true,
  ...rest
}: NavLeafItemProps) {
  const rowClasses = cn(
    NAV_LEAF_BASE,
    active ? NAV_LEAF_ACTIVE : NAV_LEAF_INACTIVE,
    className,
  );
  const iconClasses = cn(NAV_ICON_BASE, active && NAV_ICON_ACTIVE);
  const labelClasses = cn('flex-1 text-left', truncate && 'truncate');

  const inner = (
    <>
      <Icon className={iconClasses} />
      <span className={labelClasses}>{label}</span>
      {rightSlot}
    </>
  );

  if (href) {
    return (
      <Link href={href} onClick={onClick as never} className={rowClasses} {...rest}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick as never} className={rowClasses} {...rest}>
      {inner}
    </button>
  );
}
